import { useState, useEffect } from "react";
import { usePostService } from "../services/postService_compatible";
import {
  PostWithCategories,
  PostCategory,
  LanguageCode,
} from "../types/post";
import { useLanguage } from "../providers/languageProvider";

export const usePostManagement = () => {
  const postService = usePostService();
  const { currentLanguage } = useLanguage();
  const [posts, setPosts] = useState<PostWithCategories[]>([]);
  const [categories, setCategories] = useState<PostCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showCategoryEditForm, setShowCategoryEditForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<PostCategory | null>(null);

  // Load posts and categories on component mount
  useEffect(() => {
    loadData();
  }, [currentLanguage]); // Reload when language changes

  const loadData = async () => {
    try {
      setLoading(true);
      const [postsData, categoriesData] = await Promise.all([
        postService.getPosts(),
        postService.getPostCategories(currentLanguage),
      ]);
      setPosts(postsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error loading data:", error);
      alert("Nie udało się załadować danych");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (id: string) => {
    if (!confirm("Czy na pewno chcesz usunąć ten post?")) return;
    await postService.deletePost(id);
    loadData(); // Refresh the list
  };

  const handleCreateCategory = async (
    translations: { name: Record<LanguageCode, string>; description?: Record<LanguageCode, string> },
    groupKey?: string,
    icon?: string,
    color?: string
  ) => {
    await postService.createPostCategory(translations, groupKey, icon, color);
    setShowCategoryForm(false);
    loadData(); // Refresh categories
  };

  const handleUpdateCategory = async (
    categoryId: string,
    translations: { name: Record<LanguageCode, string>; description?: Record<LanguageCode, string> },
    groupKey?: string,
    icon?: string,
    color?: string
  ) => {
    await postService.updatePostCategory(categoryId, translations, groupKey, icon, color);
    setShowCategoryEditForm(false);
    setEditingCategory(null);
    loadData(); // Refresh categories
  };

  const handleEditCategory = (category: PostCategory) => {
    setEditingCategory(category);
    setShowCategoryEditForm(true);
  };

  const handleDeleteCategory = async (id: string) => {
    if (
      !confirm(
        "Czy na pewno chcesz usunąć tę kategorię? Zostanie ona usunięta ze wszystkich postów."
      )
    )
      return;
    await postService.deletePostCategory(id);
    loadData(); // Refresh the list
  };

  const openNewCategoryForm = () => {
    setShowCategoryForm(true);
  };

  return {
    // State
    posts,
    categories,
    loading,
    showCategoryForm,
    showCategoryEditForm,
    editingCategory,

    // Actions
    handleDeletePost,
    handleCreateCategory,
    handleUpdateCategory,
    handleEditCategory,
    handleDeleteCategory,
    openNewCategoryForm,
    setShowCategoryForm,
    setShowCategoryEditForm,
  };
};
